import { Flex, View, Text } from "@adobe/react-spectrum";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { RootState } from "../../../store";
import { setConsultType } from "../../../actions/common";
import storeRTK from "../../../store/storeRTK";
import { IntervalType } from "../../../types/generic.types";
import { SpeedDialData } from "../../../types/generic.types";
import { CALLBACK, CASE_STATES } from "../../../constants/constants";
import { setHistoryfilterCallbackCount } from "../../../actions/callback";
import { setHistoryfilterVoicemailCount } from "../../../actions/voicemail";
import CVDialPopover from "../CVDialPopover/CVDialPopover";
import ActiveCallContainer from "../../CallTab/CallDialer/ActiveCallContainer";
import AcceptDialog from "../AcceptDialog/AcceptDialog";
import InteractionDetails from "../InteractionDetails/InteractionDetails";
import CallbackIcon from "../../../assets/CallbackActiveIcon.svg";
import { formattedTime } from "../../../utils";

type CVContainerProps = {
  type: "callback" | "voicemail";
};

const CVContainer = ({ type }: CVContainerProps) => {
  const state = useSelector((state: RootState) =>
    type === CALLBACK ? state.callback : state.voicemail
  );
  const history = useSelector((state: RootState) => state.history);
  const speedDials = useSelector(
    (state: RootState) => state.history.speedDials
  );

  const [number, setNumber] = useState("");
  const [callTime, setCallTime] = useState(0);

  const isCallback = type === CALLBACK;

  useEffect(() => {
    if (isCallback) {
      storeRTK.dispatch(setConsultType(""));
    }
  }, []);

  useEffect(() => {
    const items = isCallback ? history?.callback : history?.voicemail;
    if (items?.length > 0) {
      const action = isCallback
        ? setHistoryfilterCallbackCount
        : setHistoryfilterVoicemailCount;
      storeRTK.dispatch(action(items.length));
    }
    setCallTime(0);
  }, [history, state?.id]);

  useEffect(() => {
    let timerId: IntervalType = null;
    if (state?.selfState === CASE_STATES.ESTABLISHED) {
      const startTime =
        state?.self?.connectedTime || state?.self?.[type]?.connectedTime;
      timerId = setInterval(() => {
        setCallTime(Date.now() - startTime);
      }, 1000);
    }
    return () => {
      if (timerId !== null) clearInterval(timerId);
    };
  }, [state]);

  useEffect(() => {
    const phone =
      state?.customer?.attributes?.PHONE_NUMBER ||
      state?.customer?.attributes?.CustomerANI ||
      "";
    setNumber(phone);
  }, [state?.id]);

  const handleNumberChange = (value: string) => {
    setNumber(value);
  };

  const getCustomerName = () => {
    // Get phone number for speed dial lookup
    const phoneNumber = state?.customer?.attributes?.PHONE_NUMBER ||
      state?.customer?.attributes?.CustomerANI ||
      "";
    
    // Clean phone number for proper matching (remove special characters except +)
    const cleanPhoneNumber = phoneNumber
      ? phoneNumber.replace(/[^\d+]/g, "").replace(/\+(\d)/g, "+$1")
      : "";
    
    // Ensure the number starts with + if it has digits
    const formattedCleanNumber = cleanPhoneNumber && !cleanPhoneNumber.startsWith("+")
      ? `+${cleanPhoneNumber}`
      : cleanPhoneNumber;
    
    // First check speed dial entries for this phone number (PRIORITY)
    const speedDialEntry = speedDials?.find(
      (entry: SpeedDialData) => entry?.phoneNumber === formattedCleanNumber
    );
    
    if (speedDialEntry && speedDialEntry.firstName && speedDialEntry.lastName) {
      return `${speedDialEntry.firstName} ${speedDialEntry.lastName}`;
    }
    
    // Fall back to customer attributes if speed dial doesn't have names
    if (state?.customer?.attributes?.FIRSTNAME && state?.customer?.attributes?.LASTNAME) {
      return `${state?.customer?.attributes?.FIRSTNAME} ${state?.customer?.attributes?.LASTNAME}`;
    }
    
    return "";
  };

  const getCustomerPhone = () => {
    return state?.customer?.attributes?.PHONE_NUMBER || state?.customer?.attributes?.CustomerANI || "";
  };

  const hasCustomerName = () => {
    // Get phone number for speed dial lookup
    const phoneNumber = state?.customer?.attributes?.PHONE_NUMBER ||
      state?.customer?.attributes?.CustomerANI ||
      "";
    
    // Clean phone number for proper matching (remove special characters except +)
    const cleanPhoneNumber = phoneNumber
      ? phoneNumber.replace(/[^\d+]/g, "").replace(/\+(\d)/g, "+$1")
      : "";
    
    // Ensure the number starts with + if it has digits
    const formattedCleanNumber = cleanPhoneNumber && !cleanPhoneNumber.startsWith("+")
      ? `+${cleanPhoneNumber}`
      : cleanPhoneNumber;
    
    // First check speed dial entries for this phone number (PRIORITY)
    const speedDialEntry = speedDials?.find(
      (entry: SpeedDialData) => entry?.phoneNumber === formattedCleanNumber
    );
    
    if (speedDialEntry && speedDialEntry.firstName && speedDialEntry.lastName) {
      return true;
    }
    
    // Fall back to customer attributes if speed dial doesn't have names
    return !!(state?.customer?.attributes?.FIRSTNAME && state?.customer?.attributes?.LASTNAME);
  };

  return (
    <>
      <Flex width="100%" height="100%" gap="20px">
        <View backgroundColor="static-white" width="30%" flex={1} height="100%">
          <Flex
            height="100%"
            width="100%"
            direction="column"
            justifyContent="space-between"
            gap="20px"
          >
            {state?.id ? (
              <View paddingTop="1rem" paddingStart="1rem">
                <View
                  borderColor="gray-200"
                  borderWidth="thin"
                  backgroundColor="gray-100"
                  borderRadius="regular"
                  UNSAFE_className="call-inProgress-container"
                >
                  <View
                    borderStartWidth="thicker"
                    borderStartColor="static-green-500"
                    borderRadius="regular"
                    UNSAFE_className="call-inProgress-mid-container"
                  >
                    <Flex
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      height="100%"
                    >
                      <Flex direction="column">
                        <Text UNSAFE_className="call-inProgress-heading">
                          {hasCustomerName() ? getCustomerName() : getCustomerPhone()}
                        </Text>
                        {hasCustomerName() && (
                          <Text UNSAFE_className="call-inProgress-font">
                            {getCustomerPhone()}
                          </Text>
                        )}
                        <Text UNSAFE_className="call-inProgress-font">
                          <Flex
                            direction="row"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Text marginX={3}>
                              {state?.selfState !== CASE_STATES.ESTABLISHED
                                ? "Ringing"
                                : `on ${type}`}
                            </Text>
                            <Text
                              UNSAFE_className="call-inprogress-timer"
                              marginX={2}
                            >
                              {formattedTime(callTime)}
                            </Text>
                            <Text>
                              <img
                                className="call-inProgress-icon"
                                src={CallbackIcon}
                                alt="CallbackIcon"
                              />
                            </Text>
                          </Flex>
                        </Text>
                      </Flex>
                      <Flex direction="row">
                        <CVDialPopover
                          type={type}
                          number={number}
                          handleNumberChange={handleNumberChange}
                        />
                      </Flex>
                    </Flex>
                    {!isCallback && state?.playbackMediaURL && (
                      <audio
                        src={state.playbackMediaURL}
                        controls
                        title=""
                        style={{
                          width: "100%",
                          marginTop: "1rem",
                          marginLeft: "-1rem",
                          paddingLeft: "1rem",
                        }}
                      />
                    )}
                  </View>
                </View>
              </View>
            ) : (
              <Flex
                alignItems="center"
                justifyContent="center"
                width="90%"
                height="100%"
              >
                <ActiveCallContainer
                  title={`No active ${type}`}
                  description={`Wait for a ${type}`}
                />
              </Flex>
            )}
          </Flex>
        </View>

        {state?.id && (
          <Flex gap="20px" width="70%">
            <View
              backgroundColor="static-white"
              gridArea="call detail"
              flex={2}
              paddingTop="1rem"
            >
              <InteractionDetails type={type} />
            </View>
          </Flex>
        )}
      </Flex>

      {state?.selfState === CASE_STATES.RINGING && <AcceptDialog type={type} />}
    </>
  );
};

export default CVContainer;
